import { useState } from 'react';
import type { Hole18ScenariosData } from '@/lib/golfEngine';
import { ChevronUp, ChevronDown, Flag } from 'lucide-react';

function formatDollar(amount: number): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${sign}$${Math.abs(amount).toFixed(0)}`;
}

function formatPressNet(net: number): string {
  if (net > 0) return `+${net}P`;
  if (net < 0) return `${net}P`;
  return 'EP';
}

function netColor(amount: number): string {
  if (amount > 0) return 'text-green-700';
  if (amount < 0) return 'text-amber-400';
  return 'text-gray-600';
}

interface Hole18ScenariosProps {
  data: Hole18ScenariosData;
}

export function Hole18Scenarios({ data }: Hole18ScenariosProps) {
  const [expanded, setExpanded] = useState(true);

  if (!expanded) {
    return (
      <div
        className="fixed left-0 right-0 z-40 bg-[#FACC15] text-[#064E3B] px-4 py-3 cursor-pointer shadow-lg"
        style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
        onClick={() => setExpanded(true)}
        data-testid="banner-hole18-scenarios"
      >
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-[#064E3B]" />
            <span className="text-sm font-bold">
              Hole {data.lastHole} Scenarios
            </span>
            <span className="text-xs text-[#064E3B]/80">— Tap to expand</span>
          </div>
          <ChevronUp className="w-4 h-4 text-[#064E3B]/80" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed left-0 right-0 z-40 shadow-2xl"
      style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
      data-testid="panel-hole18-scenarios"
    >
      <div
        className="bg-[#FACC15] text-[#064E3B] px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(false)}
      >
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-[#064E3B]" />
            <span className="text-sm font-bold">
              Hole {data.lastHole} — What's at Stake
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-[#064E3B]/80" />
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 px-4 py-3 max-h-[60dvh] overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-3">
          {data.scenarios.map((scenario, idx) => (
            <div key={idx} className="rounded-lg border border-gray-200 p-3" data-testid={`scenario-${idx}`}>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                {scenario.label}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                  <span className="text-xs font-medium text-gray-700 truncate mr-2">
                    {data.teamALabel}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-bold ${netColor(scenario.teamANet)}`}>
                      {formatDollar(scenario.teamANet)}
                    </span>
                    <span className={`text-xs font-semibold ${netColor(scenario.teamAPressNet)}`}>
                      ({formatPressNet(scenario.teamAPressNet)})
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                  <span className="text-xs font-medium text-gray-700 truncate mr-2">
                    {data.teamBLabel}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-bold ${netColor(scenario.teamBNet)}`}>
                      {formatDollar(scenario.teamBNet)}
                    </span>
                    <span className={`text-xs font-semibold ${netColor(scenario.teamBPressNet)}`}>
                      ({formatPressNet(scenario.teamBPressNet)})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="press-options">
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">
              Press Options
            </p>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-700">{data.teamALabel} can press:</span>
                <span className={`text-xs font-bold ${data.teamACanPress ? 'text-green-700' : 'text-gray-400'}`}>
                  {data.teamACanPress ? `Yes (up to ${data.teamAMaxPresses})` : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-700">{data.teamBLabel} can press:</span>
                <span className={`text-xs font-bold ${data.teamBCanPress ? 'text-green-700' : 'text-gray-400'}`}>
                  {data.teamBCanPress ? `Yes (up to ${data.teamBMaxPresses})` : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
